import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppraisalSettingsDto } from '../models/appraisal-settings.dto';
import { AppraisalManagerResponseDto } from '../models/appraisal-manager-response.dto';
import { AppraisalEmployeeResponseDto } from '../models/appraisal-employee-response.dto';
import { AppraisalMeetingEmployeeOptionDto } from '../models/appraisal-meeting-employee-option.dto';
import { AppraisalAssignmentRequestDto } from '../models/appraisal-assignment-request.dto';
import { AppraisalAssignmentRequestDecisionDto } from '../models/appraisal-assignment-request-decision.dto';
import { AppraisalRelationshipStatus } from '../models/appraisal-relationship-status.enum';
import { map } from 'rxjs/operators';


@Injectable()
export class AppraisalSettingsService {
  private readonly API_BASE = `${environment.BASE_URL_V2}/appraisal/settings`;

  constructor(private http: HttpClient) {}

  /**
   * Returns all employees assigned to the given manager, regardless of status.
   * Filter by Status === Active (1) to get only accepted relationships.
   */
  getAssignedEmployees(managerId: number): Observable<AppraisalEmployeeResponseDto[]> {
    return this.http
      .get<any>(`${this.API_BASE}/manager/${managerId}/employees`)
      .pipe(
        map((result: any) => this.toArray(result).map(raw => this.normalizeEmployeeResponse(raw)))
      );
  }

  getMeetingEmployees(
    managerId: number,
    templateId?: number | null
  ): Observable<AppraisalEmployeeResponseDto[]> {
    const options = this.buildTemplateScopeOptions(templateId);

    return this.http
      .get<any>(`${this.API_BASE}/manager/${managerId}/meeting-employees`, options)
      .pipe(
        map((result: any) => this.toArray(result).map(raw => this.normalizeEmployeeResponse(raw)))
      );
  }

  /**
   * Returns every active business employee with the current manager relationship status
   * (null when there is no relationship yet). Used by the Dialog Meeting create screen.
   */
  getBusinessEmployeesForMeetingCreation(
    managerId: number,
    templateId?: number | null
  ): Observable<AppraisalMeetingEmployeeOptionDto[]> {
    const options = this.buildTemplateScopeOptions(templateId);

    return this.http
      .get<any>(`${this.API_BASE}/manager/${managerId}/business-employees`, options)
      .pipe(
        map((result: any) =>
          this.toArray(result).map(raw => this.normalizeMeetingEmployeeOption(raw))
        )
      );
  }

  /**
   * Returns all managers associated with the given business.
   */
  getManagersByBusiness(businessId: number): Observable<AppraisalManagerResponseDto[]> {
    return this.http
      .get<any>(`${this.API_BASE}/${businessId}/managers`)
      .pipe(
        map((result: any) => this.toArray(result).map(raw => this.normalizeManagerResponse(raw)))
      );
  }

  private handleError(error: HttpErrorResponse) {
    console.error('AppraisalSettingsService Error:', error);
     throwError(
      () => new Error(error.error?.message ?? 'An error occurred')
    );
  }

  getBusinessSettings(businessId: number): Observable<AppraisalSettingsDto> {
    return this.http.get<AppraisalSettingsDto>(
      `${this.API_BASE}/${businessId}`
    );
  }

  canManageSettings(businessId: number): Observable<boolean> {
    return this.http.get<boolean>(
      `${environment.BASE_URL_V2}/appraisal/access/business/${businessId}/manage-settings`
    );
  }

  /**
   * Fetches the list of managers for a specific business by its ID.
   * This is used to populate the main table of managers in the appraisal settings component.
   * @param businessId ID of the business for which to fetch the managers
   * @returns Observable of AppraisalManagerResponseDto
   */
  getManagers(businessId: number): Observable<AppraisalManagerResponseDto[]> {
    return this.http.get<any>(
      `${this.API_BASE}/${businessId}/managers`
    ).pipe(
      map((result: any) => this.toArray(result).map(raw => this.normalizeManagerResponse(raw)))
    );
  }

  /**
   * Updates the list of managers assigned to a specific business. This is used when adding
   * or removing managers for the business.
   * @param businessId ID of the business for which to update the managers
   * @param managerIds IDs of the managers to be assigned to the business
   * @returns Observable of void
   */
  updateManagersForBusiness(
    businessId: number,
    managerIds: number[]
  ): Observable<void> {
    return this.http.put<void>(
      `${this.API_BASE}/${businessId}/managers`,
      managerIds
    );
  }

  /**
   * Updates the list of employees assigned to a specific manager. This is used when updating the
   * assignments for a manager.
   * @param userId ID of the manager user (Editor assigned as manager)
   * @param employeeIds List of employee-user IDs to be assigned to the manager. This will replace the existing
   * list of assigned employees for the manager.
   * @returns Observable of void
   */
  updateEmployeesForManager(
    userId: number,
    employeeIds: number[]
  ): Observable<void> {
    return this.http.put<void>(
      `${this.API_BASE}/manager/${userId}/employees`,
      employeeIds
    );
  }

  /**
   * Returns assignment requests where the current user is the target employee.
   */
  getMyRequests(): Observable<AppraisalAssignmentRequestDto[]> {
    return this.http
      .get<AppraisalAssignmentRequestDto[]>(`${this.API_BASE}/my-requests`)
      .pipe(map((result: any) => result));
  }

  /**
   * Approves or rejects a pending manager assignment request for the current user.
   */
  respondToMyRequest(
    relationshipId: number,
    decision: AppraisalAssignmentRequestDecisionDto
  ): Observable<void> {
    return this.http
      .patch<void>(
        `${this.API_BASE}/my-requests/${relationshipId}/decision`,
        decision
      );
  }

  private normalizeEmployeeResponse(raw: any): AppraisalEmployeeResponseDto {
    const status = this.normalizeRelationshipStatus(raw?.Status ?? raw?.status);

    return {
      Id: Number(raw?.Id ?? raw?.id ?? 0),
      EmployeeUserId: Number(raw?.EmployeeUserId ?? raw?.employeeUserId ?? 0),
      Name: String(raw?.Name ?? raw?.name ?? ''),
      Departments: raw?.Departments ?? raw?.departments ?? [],
      Status: status,
      RequestedAt: raw?.RequestedAt ?? raw?.requestedAt,
      RespondedAt: raw?.RespondedAt ?? raw?.respondedAt,
      AssignedBy: raw?.AssignedBy ?? raw?.assignedBy,
      RejectionReason: raw?.RejectionReason ?? raw?.rejectionReason ?? ''
    };
  }

  private normalizeMeetingEmployeeOption(raw: any): AppraisalMeetingEmployeeOptionDto {
    const rawStatus = raw?.RelationshipStatus ?? raw?.relationshipStatus;
    const relationshipStatus =
      rawStatus === null || rawStatus === undefined
        ? null
        : this.normalizeRelationshipStatus(rawStatus);

    const rawRelationshipId = raw?.RelationshipId ?? raw?.relationshipId;

    return {
      EmployeeUserId: Number(raw?.EmployeeUserId ?? raw?.employeeUserId ?? 0),
      Name: String(raw?.Name ?? raw?.name ?? ''),
      Departments: raw?.Departments ?? raw?.departments ?? [],
      RelationshipStatus: relationshipStatus,
      RelationshipId:
        rawRelationshipId === null || rawRelationshipId === undefined
          ? null
          : Number(rawRelationshipId)
    };
  }

  private normalizeManagerResponse(raw: any): AppraisalManagerResponseDto {
    return {
      Id: Number(raw?.Id ?? raw?.id ?? 0),
      Name: String(raw?.Name ?? raw?.name ?? ''),
      AssignmentCount: Number(raw?.AssignmentCount ?? raw?.assignmentCount ?? 0),
      IsAdminManaged: Boolean(raw?.IsAdminManaged ?? raw?.isAdminManaged ?? false)
    };
  }

  private normalizeRelationshipStatus(rawStatus: any): AppraisalRelationshipStatus {
    if (typeof rawStatus === 'number') {
      return rawStatus as AppraisalRelationshipStatus;
    }

    if (typeof rawStatus === 'string') {
      const trimmed = rawStatus.trim();
      const numeric = Number(trimmed);
      if (!Number.isNaN(numeric)) {
        return numeric as AppraisalRelationshipStatus;
      }

      switch (trimmed.toLowerCase()) {
        case 'active':
          return AppraisalRelationshipStatus.Active;
        case 'pending':
          return AppraisalRelationshipStatus.Pending;
        case 'rejected':
          return AppraisalRelationshipStatus.Rejected;
        case 'revoked':
          return AppraisalRelationshipStatus.Revoked;
      }
    }

    return AppraisalRelationshipStatus.Pending;
  }

  private toArray(result: any): any[] {
    if (Array.isArray(result)) {
      return result;
    }

    if (Array.isArray(result?.$values)) {
      return result.$values;
    }

    if (Array.isArray(result?.Items)) {
      return result.Items;
    }

    if (Array.isArray(result?.items)) {
      return result.items;
    }

    return [];
  }

  private buildTemplateScopeOptions(templateId?: number | null): { params?: HttpParams } {
    if (!templateId || templateId <= 0) {
      return {};
    }

    return {
      params: new HttpParams().set('templateId', String(templateId))
    };
  }
}
